import { type ReactNode, useEffect, useState } from 'react';
import { useStatuteAntiScrapeDocument } from '../hooks/useStatuteAntiScrapeDocument';

function automationLikely(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { webdriver?: boolean };
  if (nav.webdriver === true) return true;
  // Headless Chromium often reports a single-language empty plugins surface; keep heuristic narrow.
  if (/HeadlessChrome/i.test(nav.userAgent || '')) return true;
  return false;
}

/**
 * Applies statute-route document guards. Optionally blocks obvious automation when
 * `VITE_STATUTE_BLOCK_AUTOMATION_BROWSERS=true` (e.g. production only).
 */
export function StatuteAntiScrapeBoundary({ children }: { children: ReactNode }) {
  useStatuteAntiScrapeDocument();
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const enforce = import.meta.env.VITE_STATUTE_BLOCK_AUTOMATION_BROWSERS === 'true';
    if (enforce && automationLikely()) setBlocked(true);
  }, []);

  if (blocked) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-950 text-zinc-100 px-6 text-center">
        <p className="text-sm font-semibold tracking-wide uppercase text-zinc-400 mb-2">Access restricted</p>
        <h1 className="text-xl font-bold max-w-md mb-3">This content cannot be opened in an automated browser.</h1>
        <p className="text-zinc-400 text-sm max-w-lg leading-relaxed">
          LexGat statute text is available to human visitors only. If you believe this is an error, open this page
          in a standard desktop or mobile browser.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
